import type { CallScriptVerifiedFields } from "./callScript.types";

export const PHONE_DIGITS_LENGTH = 10;
export const ZIP_MASK_PLACEHOLDER = "00000";

type ValidatedField =
  | "employerName"
  | "phoneNumberDialed"
  | "hireDate"
  | "zip"
  | "employerPhone"
  | "workLocationPhone"
  | "dateOfTermination";

export type CallScriptFieldStates = Partial<Record<ValidatedField, "ok" | "error">>;

export interface CallScriptValidationResult {
  isValid: boolean;
  fieldStates: CallScriptFieldStates;
}

/**
 * Blank/whitespace-only/mask-placeholder values are treated as "not entered"
 * (optional fields pass; required fields fail separately).
 * @param {string} value - The raw form value.
 * @param {string} [placeholder] - The input's mask placeholder, if any.
 * @returns {string | undefined} The trimmed value, or `undefined` if not entered.
 */
function normalize(value: string, placeholder?: string): string | undefined {
  const trimmed = value.trim();
  if (trimmed === "" || (placeholder !== undefined && trimmed === placeholder)) {
    return undefined;
  }
  return trimmed;
}

/**
 * Phone fields are stored as a canonical digits-only string (see
 * VerifiedControl's "phone" case and PhoneNumberInput) so entered and saved
 * values always compare in the same format — a full, unambiguous US number
 * is exactly 10 digits.
 * @param {string} value - The digits-only value to check.
 * @returns {boolean} Whether it's a complete 10-digit number.
 */
export function isValidPhoneNumber(value: string): boolean {
  return /^\d+$/.test(value) && value.length === PHONE_DIGITS_LENGTH;
}

/**
 * DateInput (uikit's DatePicker) only ever writes a valid `YYYY-MM-DD` or `""`
 * into form state — it already blocks partial/invalid-calendar input before
 * `onChange` fires — so this is a defensive re-check rather than a real
 * gate in this codebase, unlike the spec's raw `mm/dd/yyyy`-masked-string case.
 * @param {string} value - The date string to validate.
 * @returns {boolean} Whether it's a real, non-rolled-over `YYYY-MM-DD` calendar date.
 */
export function isValidIsoDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return false;
  }
  const [, yearString, monthString, dayString] = match;
  const year = Number(yearString);
  const month = Number(monthString);
  const day = Number(dayString);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/**
 * Submit-time validation for the Call Script "Verified Information" fields
 * (OR-21767 spec). Every field is checked independently — no short-circuit —
 * so all invalid fields can be flagged for per-field error styling at once.
 *
 * `employerName` is only required when there's no Saved Information Employer
 * Name to fall back on — if the Employment record already has one, the
 * analyst isn't forced to retype it.
 *
 * TODO(OR-21767): the spec also validates a Temp Agency Address zip.
 * There is no Temp Agency form field yet — CallScriptVerifiedFields has
 * only the one address (`zip`) — so that spec rule is intentionally
 * unmapped until the field exists.
 * @param {CallScriptVerifiedFields} verified - The current "Verified Information" form values.
 * @param {CallScriptVerifiedFields} saved - The current "Saved Information" form values.
 * @returns {CallScriptValidationResult} Overall validity plus a per-field ok/error state.
 */
export function validateEmploymentVerification(
  verified: CallScriptVerifiedFields,
  saved: CallScriptVerifiedFields
): CallScriptValidationResult {
  const fieldStates: CallScriptFieldStates = {};

  const employerName = normalize(verified.employerName);
  const savedEmployerName = normalize(saved.employerName);
  fieldStates.employerName = employerName === undefined && savedEmployerName === undefined ? "error" : "ok";

  const phoneNumberDialed = normalize(verified.phoneNumberDialed);
  fieldStates.phoneNumberDialed =
    phoneNumberDialed !== undefined && isValidPhoneNumber(phoneNumberDialed) ? "ok" : "error";

  const hireDate = normalize(verified.hireDate);
  fieldStates.hireDate = hireDate === undefined || isValidIsoDate(hireDate) ? "ok" : "error";

  const zip = normalize(verified.zip, ZIP_MASK_PLACEHOLDER);
  fieldStates.zip = zip === undefined || zip.length >= 5 ? "ok" : "error";

  const employerPhone = normalize(verified.employerPhone);
  fieldStates.employerPhone = employerPhone === undefined || isValidPhoneNumber(employerPhone) ? "ok" : "error";

  const workLocationPhone = normalize(verified.workLocationPhone);
  fieldStates.workLocationPhone =
    workLocationPhone === undefined || isValidPhoneNumber(workLocationPhone) ? "ok" : "error";

  const dateOfTermination = normalize(verified.dateOfTermination);
  fieldStates.dateOfTermination = dateOfTermination === undefined || isValidIsoDate(dateOfTermination) ? "ok" : "error";

  const isValid = Object.values(fieldStates).every((state) => state === "ok");
  return { isValid, fieldStates };
}
